import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store';
import { useAuthHydrated } from '@/hooks/useRequireAuth';
import { consumeHandoff } from '../../shared/public/auth-browser.cjs';
import { trackUser, trackWhopCompleteRegistration } from '@/constants/utils/analytics';
import { trackAuthEvent, AUTH_ACTION } from '@/constants/utils/analyticsEvents';

export default function usePublicAuthHandoff(pathname) {
  const hydrated = useAuthHydrated();
  // Do not suppress the retained catalog pages' readable server-rendered HTML.
  // Only the app/checkout entry can receive a public-auth session transfer.
  const [state, setState] = useState(() => ({ pending: ['/app', '/payment-successful'].includes(pathname
    || (typeof window !== 'undefined' ? window.location.pathname : '')), error: null }));
  useEffect(() => {
    if (!hydrated) return;
    let disposed = false;
    (async () => { try {
      if (!['/app', '/payment-successful'].includes(window.location.pathname)) {
        setState({ pending: false, error: null }); return;
      }
      if (window.location.pathname === '/app' && new URLSearchParams(window.location.search).get('publicAuth') === 'signup') {
        const store = useAuthStore.getState();
        if (store.user?.flags?.isGhost) {
          const token = await store.getValidToken({ reconnectSocket: false });
          if (!token) throw Error('Your guest session could not be restored. Please log in or try again.');
        }
        if (!disposed) window.location.replace('/signup');
        return;
      }
      const record = consumeHandoff(window.sessionStorage);
      if (record) {
        // Reuse all existing account-switch cleanup, token normalization,
        // verification gating and persistence. Pug never writes auth-storage.
        useAuthStore.getState().setAuthData(record.data.tokens.access,
          record.data.tokens.expiresAt, record.data.tokens.refresh, record.data.user);
        const event = record.data.publicAuth;
        try { if (event && ['email', 'ghost_upgrade', 'google', 'apple'].includes(event.method)) {
          trackUser(record.data.user.id);
          trackAuthEvent(event.signup ? AUTH_ACTION.SIGNUP_SUCCESS : AUTH_ACTION.LOGIN_SUCCESS, event.method);
          if (event.signup) trackWhopCompleteRegistration();
        } } catch { /* Analytics cannot prevent an authenticated app handoff. */ }
      }
      if (!disposed) setState({ pending: false, error: null });
    } catch (error) { if (!disposed) setState({ pending: false, error: error.message }); }
    })();
    return () => { disposed = true; };
  }, [hydrated]);
  return state;
}
